import { onMeasurementConsent } from './consent'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID

// Two independent switches, both of which must be off for anything to be sent:
// the visitor's answer to the cookie banner, and our own opt-out for staff so
// internal usage does not pollute the numbers. Consent is the one that carries
// legal weight; the opt-out is housekeeping.
let _consented = false
let _adminOptOut = false
let _loaded = false

function trackingAllowed() {
  return Boolean(GA_ID) && _consented && !_adminOptOut
}

function loadGtag() {
  if (!GA_ID || _loaded || document.querySelector('script[src*="googletagmanager"]')) return
  _loaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())
  window.gtag('config', GA_ID, { send_page_view: false })
}

/**
 * Deletes the identifiers GA left behind. Revoking consent has to remove what
 * was already stored, not merely stop adding to it — a cookie the visitor has
 * refused cannot survive on the device.
 *
 * GA writes on the registrable domain, so the same name is cleared against
 * every parent of the current host.
 */
function clearMeasurementCookies() {
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => /^_ga(_|$)/.test(name) || /^_gat(_|$)/.test(name) || name === '_gid')

  if (names.length === 0) return

  const host = window.location.hostname
  const parts = host.split('.')
  const domains: (string | null)[] = [null, host]
  for (let i = 0; i < parts.length - 1; i++) {
    domains.push(`.${parts.slice(i).join('.')}`)
  }

  names.forEach((name) => {
    domains.forEach((domain) => {
      const scope = domain ? `; domain=${domain}` : ''
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${scope}`
    })
  })
}

function applyState() {
  if (!GA_ID) return

  // gtag honours this global on every call, which is what makes a mid-session
  // withdrawal take effect without a reload.
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = !trackingAllowed()

  if (trackingAllowed()) {
    loadGtag()
  } else {
    clearMeasurementCookies()
  }
}

/**
 * Subscribes analytics to the consent gate. Safe to call at boot: it loads
 * nothing on its own, and gtag only reaches the page if and when the visitor
 * has said yes.
 */
export function initAnalytics() {
  if (!GA_ID) return

  onMeasurementConsent((granted) => {
    _consented = granted
    applyState()
  })
}

/**
 * Keeps staff out of the numbers. Independent of consent: an admin who accepted
 * cookies still should not be counted.
 */
export function setAdminOptOut(optOut: boolean) {
  _adminOptOut = optOut
  applyState()
}

export function trackPageView(path: string) {
  if (!trackingAllowed() || !window.gtag) return
  window.gtag('event', 'page_view', { page_path: path })
}

export function trackEvent(name: string, params?: Record<string, unknown>) {
  if (!trackingAllowed() || !window.gtag) return
  window.gtag('event', name, params)
}
